import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { 
  Phone, PhoneOff, Mic, MicOff, Bot, 
  CheckCircle2, Loader2, KeyRound, Calendar,
  Clock, AlertTriangle, CalendarCheck, ChevronLeft, ChevronRight,
  Sparkles
} from 'lucide-react';
import PostCallAvatar from './PostCallAvatar';

const API = process.env.REACT_APP_BACKEND_URL;

// Check for browser speech recognition support - with better error handling
let SpeechRecognition = null;
let hasSpeechRecognition = false;

try {
  SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  hasSpeechRecognition = !!SpeechRecognition;
} catch (e) {
  console.log('Speech Recognition not available');
}

// Post-call step enum
const PostCallStep = {
  DISPOSITION: 'disposition',
  SCHEDULE: 'schedule',
  CONFIRM: 'confirm'
};

// Post-call mode
const PostCallMode = {
  AVATAR: 'avatar',      // Interactive avatar conversation
  CLASSIC: 'classic'     // Original step-by-step form
};

export const CallAssistantDialog = ({ 
  open, 
  onClose, 
  lead, 
  pipelineStages, 
  onUpdateLead
}) => {
  // Call state
  const [callState, setCallState] = useState('idle'); // idle, calling, ended
  const [callDuration, setCallDuration] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [disposition, setDisposition] = useState('');
  const [selectedNextStage, setSelectedNextStage] = useState('');
  
  // Post-call mode state - defaults to avatar for better UX
  const [postCallMode, setPostCallMode] = useState(PostCallMode.AVATAR);
  
  // AI state
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [loadingKey, setLoadingKey] = useState(true);
  
  // Post-call interactive state
  const [postCallStep, setPostCallStep] = useState(PostCallStep.DISPOSITION);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [followUpTitle, setFollowUpTitle] = useState('');
  const [followUpType, setFollowUpType] = useState('follow_up');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [savingEvent, setSavingEvent] = useState(false);
  
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const startTimeRef = useRef(null);
  const speechRecognitionRef = useRef(null);

  useEffect(() => {
    if (open) {
      resetState();
      fetchAISettings();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopRecording();
    };
  }, [open]);

  // Fetch available time slots when date changes
  useEffect(() => {
    if (selectedDate && showScheduler) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, showScheduler]);

  // Check for conflicts when time is selected
  useEffect(() => {
    if (selectedDate && selectedTime) {
      checkConflicts();
    }
  }, [selectedDate, selectedTime]);

  const fetchAISettings = async () => {
    setLoadingKey(true);
    try {
      const token = localStorage.getItem('token');
      const settingsRes = await fetch(`${API}/api/ai-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setAiEnabled(settings.ai_enabled);
        
        if (settings.ai_enabled) {
          const keyRes = await fetch(`${API}/api/ai-settings/key-for-user`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (keyRes.ok) {
            const keyData = await keyRes.json();
            setApiKey(keyData.api_key);
          }
        }
      }
    } catch (error) {
      console.log('AI not configured');
    } finally {
      setLoadingKey(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/calendar/availability?date=${date}&duration_minutes=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.slots || []);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const checkConflicts = async () => {
    if (!selectedDate || !selectedTime) return;
    
    try {
      const token = localStorage.getItem('token');
      const startTime = new Date(`${selectedDate}T${selectedTime}`);
      const endTime = new Date(startTime.getTime() + 30 * 60000); // 30 minutes
      
      const res = await fetch(`${API}/api/calendar/check-conflicts`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString()
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConflicts(data.conflicts || []);
      }
    } catch (error) {
      console.error('Error checking conflicts:', error);
    }
  };

  const resetState = () => {
    setCallState('idle');
    setCallDuration(0);
    setIsRecording(false);
    setDisposition('');
    setSelectedNextStage(lead?.status || '');
    setAiSuggestion(null);
    setPostCallStep(PostCallStep.DISPOSITION);
    setPostCallMode(PostCallMode.AVATAR); // Default to avatar mode
    setShowScheduler(false);
    setSelectedDate('');
    setSelectedTime('');
    setFollowUpTitle('');
    setFollowUpType('follow_up');
    setAvailableSlots([]);
    setConflicts([]);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startCall = () => {
    setCallState('calling');
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  };

  const endCall = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setCallState('ended');
    stopRecording();
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow.toISOString().split('T')[0]);
  };

  const startRecording = async () => {
    // If AI is enabled with API key, use OpenAI Whisper transcription
    if (aiEnabled && apiKey) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await transcribeAudio(audioBlob);
          stream.getTracks().forEach(track => track.stop());
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);
        toast.success('Recording started - speak your disposition');
      } catch (error) {
        console.error('Recording error:', error);
        toast.error('Could not access microphone. Please allow microphone access.');
      }
    } 
    // Otherwise use browser's built-in Speech Recognition
    else if (hasSpeechRecognition && SpeechRecognition) {
      try {
        // Clean up any existing recognition instance
        if (speechRecognitionRef.current) {
          try {
            speechRecognitionRef.current.abort();
          } catch (e) {
            // Ignore cleanup errors
          }
          speechRecognitionRef.current = null;
        }

        speechRecognitionRef.current = new SpeechRecognition();
        speechRecognitionRef.current.continuous = true;
        speechRecognitionRef.current.interimResults = true;
        speechRecognitionRef.current.lang = 'en-US';

        let finalTranscript = disposition;

        speechRecognitionRef.current.onresult = (event) => {
          let interimTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += ' ' + transcript;
              setDisposition(finalTranscript.trim());
            } else {
              interimTranscript += transcript;
            }
          }
        };

        speechRecognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          // Only show error for actual problems, not expected behaviors
          if (event.error === 'not-allowed') {
            toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
          } else if (event.error === 'network') {
            toast.error('Network error. Please check your connection.');
          } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
            toast.error('Voice recognition error. Please try again.');
          }
          setIsRecording(false);
        };

        speechRecognitionRef.current.onend = () => {
          setIsRecording(false);
        };

        speechRecognitionRef.current.start();
        setIsRecording(true);
        toast.success('Listening... Speak your call notes');
      } catch (error) {
        console.error('Speech recognition error:', error);
        setIsRecording(false);
        toast.error('Could not start voice recognition. Please try again or type your notes.');
      }
    } else {
      toast.error('Voice recognition is not supported in this browser. Please use Chrome or Edge.');
    }
  };

  const stopRecording = () => {
    // Stop MediaRecorder if using AI transcription
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        console.log('MediaRecorder cleanup:', e);
      }
      setIsRecording(false);
    }
    // Stop Speech Recognition if using browser API
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.abort();
      } catch (e) {
        console.log('SpeechRecognition cleanup:', e);
      }
      speechRecognitionRef.current = null;
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    if (!apiKey) {
      toast.error('AI is not configured. Contact your admin.');
      return;
    }

    setAiLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      formData.append('model', 'whisper-1');

      const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setDisposition(data.text);
        await getAISuggestion(data.text);
      } else {
        const error = await response.json();
        toast.error(`Transcription failed: ${error.error?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Failed to transcribe audio');
    } finally {
      setAiLoading(false);
    }
  };

  const getAISuggestion = async (dispositionText) => {
    if (!apiKey || !dispositionText) return;

    setAiLoading(true);
    try {
      const stageNames = pipelineStages.map(s => s.name).join(', ');
      const prompt = `You are a real estate CRM assistant. Based on this call disposition, suggest the next action and stage.

Lead: ${lead?.name}
Current Stage: ${pipelineStages.find(s => s.slug === lead?.status)?.name || lead?.status}
Available Stages: ${stageNames}

Call Disposition: "${dispositionText}"

Provide a brief JSON response:
{
  "suggested_stage": "stage_name",
  "reason": "brief explanation",
  "next_action": "what to do next (be specific like 'Schedule site visit' or 'Send brochure')",
  "suggested_follow_up": "follow_up|site_visit|meeting|call",
  "suggested_follow_up_title": "Brief title for the follow-up",
  "urgency": "today|tomorrow|this_week|next_week",
  "priority": "low|medium|high"
}`;

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [{ role: 'user', content: prompt }],
          max_tokens: 300,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const content = data.choices[0]?.message?.content;
        try {
          const suggestion = JSON.parse(content);
          setAiSuggestion(suggestion);
          
          // Auto-select suggested stage
          const matchedStage = pipelineStages.find(
            s => s.name.toLowerCase() === suggestion.suggested_stage?.toLowerCase()
          );
          if (matchedStage) {
            setSelectedNextStage(matchedStage.slug);
          }
          
          // Auto-fill follow-up details from AI suggestion
          if (suggestion.suggested_follow_up_title) {
            setFollowUpTitle(suggestion.suggested_follow_up_title);
          }
          if (suggestion.suggested_follow_up) {
            setFollowUpType(suggestion.suggested_follow_up);
          }
          
          // Set suggested date based on urgency
          const suggestedDate = new Date();
          switch(suggestion.urgency) {
            case 'today':
              break;
            case 'tomorrow':
              suggestedDate.setDate(suggestedDate.getDate() + 1);
              break;
            case 'this_week':
              suggestedDate.setDate(suggestedDate.getDate() + 3);
              break;
            case 'next_week':
              suggestedDate.setDate(suggestedDate.getDate() + 7);
              break;
            default:
              suggestedDate.setDate(suggestedDate.getDate() + 1);
          }
          setSelectedDate(suggestedDate.toISOString().split('T')[0]);
          
        } catch (e) {
          setAiSuggestion({ reason: content });
        }
      }
    } catch (error) {
      console.error('AI suggestion error:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleManualDispositionChange = useCallback(async (text) => {
    setDisposition(text);
    // Debounced AI suggestion - only trigger when text is substantial
    if (text.length > 30 && apiKey && !aiLoading) {
      await getAISuggestion(text);
    }
  }, [apiKey, aiLoading]);

  const handleNextStep = () => {
    if (postCallStep === PostCallStep.DISPOSITION) {
      setPostCallStep(PostCallStep.SCHEDULE);
      setShowScheduler(true);
    } else if (postCallStep === PostCallStep.SCHEDULE) {
      setPostCallStep(PostCallStep.CONFIRM);
    }
  };

  const handlePreviousStep = () => {
    if (postCallStep === PostCallStep.SCHEDULE) {
      setPostCallStep(PostCallStep.DISPOSITION);
      setShowScheduler(false);
    } else if (postCallStep === PostCallStep.CONFIRM) {
      setPostCallStep(PostCallStep.SCHEDULE);
    }
  };

  const handleSaveAndSchedule = async () => {
    if (!disposition.trim()) {
      toast.error('Please enter a disposition');
      return;
    }

    setSavingEvent(true);
    try {
      const token = localStorage.getItem('token');
      
      // 1. Log the call activity
      await fetch(`${API}/api/crm/leads/${lead.id}/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          activity_type: 'call',
          notes: disposition,
          call_duration: callDuration,
          call_outcome: aiSuggestion?.priority || 'medium',
        }),
      });

      // 2. Update lead stage if changed
      if (selectedNextStage && selectedNextStage !== lead.status) {
        await fetch(`${API}/api/crm/leads/${lead.id}/stage`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ stage: selectedNextStage }),
        });
      }

      // 3. Create calendar event if scheduled
      if (selectedDate && selectedTime && followUpTitle) {
        const startTime = new Date(`${selectedDate}T${selectedTime}`);
        const endTime = new Date(startTime.getTime() + 30 * 60000); // 30 minutes
        
        await fetch(`${API}/api/calendar/events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: followUpTitle,
            description: `Follow-up from call: ${disposition.substring(0, 200)}...`,
            event_type: followUpType,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            lead_id: lead.id,
            reminder_minutes: 30
          }),
        });
        
        toast.success('Call logged and follow-up scheduled!');
      } else {
        toast.success('Call logged successfully');
      }

      onUpdateLead && onUpdateLead();
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleSkipScheduling = async () => {
    if (!disposition.trim()) {
      toast.error('Please enter a disposition');
      return;
    }

    setSavingEvent(true);
    try {
      const token = localStorage.getItem('token');
      
      await fetch(`${API}/api/crm/leads/${lead.id}/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          activity_type: 'call',
          notes: disposition,
          call_duration: callDuration,
          call_outcome: aiSuggestion?.priority || 'medium',
        }),
      });

      if (selectedNextStage && selectedNextStage !== lead.status) {
        await fetch(`${API}/api/crm/leads/${lead.id}/stage`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ stage: selectedNextStage }),
        });
      }

      toast.success('Call logged successfully');
      onUpdateLead && onUpdateLead();
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    } finally {
      setSavingEvent(false);
    }
  };

  const formatTimeDisplay = (isoTime) => {
    const date = new Date(isoTime);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Render disposition step
  const renderDispositionStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">How was the call?</Label>
        {(hasSpeechRecognition || (aiEnabled && apiKey)) && (
          <Button
            variant={isRecording ? "destructive" : "outline"}
            size="sm"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={aiLoading}
            data-testid="btn-voice-input"
          >
            {isRecording ? (
              <>
                <MicOff className="h-4 w-4 mr-1" />
                Stop
              </>
            ) : (
              <>
                <Mic className="h-4 w-4 mr-1" />
                Voice Input
              </>
            )}
          </Button>
        )}
      </div>

      {isRecording && (
        <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm text-red-600">
            {aiEnabled && apiKey ? 'Recording... Speak your disposition' : 'Listening... Speak your call notes'}
          </span>
        </div>
      )}

      {aiLoading && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <span className="text-sm text-blue-600">Processing with AI...</span>
        </div>
      )}

      <Textarea
        value={disposition}
        onChange={(e) => handleManualDispositionChange(e.target.value)}
        placeholder="Type or speak your call notes..."
        rows={3}
        data-testid="input-disposition"
      />

      {/* AI Suggestion */}
      {aiSuggestion && (
        <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <Bot className="h-5 w-5 text-violet-600 mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="font-medium text-violet-800 text-sm">AI Suggestion</p>
                {aiSuggestion.suggested_stage && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Move to:</span>{' '}
                    <span className="font-medium">{aiSuggestion.suggested_stage}</span>
                  </p>
                )}
                {aiSuggestion.reason && (
                  <p className="text-sm text-muted-foreground">{aiSuggestion.reason}</p>
                )}
                {aiSuggestion.next_action && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Next:</span> {aiSuggestion.next_action}
                  </p>
                )}
                {aiSuggestion.urgency && (
                  <Badge variant="outline" className="text-xs">
                    {aiSuggestion.urgency === 'today' ? 'Urgent - Today' : 
                     aiSuggestion.urgency === 'tomorrow' ? 'Tomorrow' :
                     aiSuggestion.urgency === 'this_week' ? 'This Week' : 'Next Week'}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stage Selection */}
      <div className="space-y-2">
        <Label>Update Stage</Label>
        <Select value={selectedNextStage} onValueChange={setSelectedNextStage}>
          <SelectTrigger>
            <SelectValue placeholder="Select next stage" />
          </SelectTrigger>
          <SelectContent>
            {pipelineStages.map((stage) => (
              <SelectItem key={stage.slug} value={stage.slug}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: stage.color }} 
                  />
                  {stage.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  // Render schedule step
  const renderScheduleStep = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Calendar className="h-5 w-5 text-amber-600" />
        <Label className="text-base font-semibold">Schedule Next Action</Label>
      </div>

      {/* Follow-up Type */}
      <div className="space-y-2">
        <Label>Action Type</Label>
        <Select value={followUpType} onValueChange={setFollowUpType}>
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="follow_up">Follow-up Call</SelectItem>
            <SelectItem value="site_visit">Site Visit</SelectItem>
            <SelectItem value="meeting">Meeting</SelectItem>
            <SelectItem value="call">Callback</SelectItem>
            <SelectItem value="reminder">Reminder</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label>Title</Label>
        <Input
          value={followUpTitle}
          onChange={(e) => setFollowUpTitle(e.target.value)}
          placeholder={`Follow-up with ${lead?.name}`}
          data-testid="input-followup-title"
        />
      </div>

      {/* Date Selection */}
      <div className="space-y-2">
        <Label>Date</Label>
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
          data-testid="input-date"
        />
      </div>

      {/* Time Slots */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Available Time Slots
        </Label>
        
        {loadingSlots ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : availableSlots.length > 0 ? (
          <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
            {availableSlots.map((slot, index) => (
              <Button
                key={index}
                variant={selectedTime === slot.start_time.split('T')[1]?.substring(0, 5) ? "default" : slot.is_available ? "outline" : "ghost"}
                size="sm"
                className={`text-xs ${!slot.is_available ? 'opacity-50 line-through' : ''}`}
                disabled={!slot.is_available}
                onClick={() => setSelectedTime(slot.start_time.split('T')[1]?.substring(0, 5))}
              >
                {formatTimeDisplay(slot.start_time)}
              </Button>
            ))}
          </div>
        ) : selectedDate ? (
          <p className="text-sm text-muted-foreground text-center py-2">
            No slots available. Select a different date.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-2">
            Select a date to see available slots
          </p>
        )}
      </div>

      {/* Conflict Warning */}
      {conflicts.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Conflict detected: {conflicts[0].title} at this time
          </AlertDescription>
        </Alert>
      )}

      {/* Selected Summary */}
      {selectedDate && selectedTime && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800">
                  {new Date(`${selectedDate}T${selectedTime}`).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric' 
                  })} at {formatTimeDisplay(`${selectedDate}T${selectedTime}`)}
                </p>
                <p className="text-sm text-green-600">{followUpTitle || `Follow-up with ${lead?.name}`}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Render confirm step
  const renderConfirmStep = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-green-600" />
        <Label className="text-base font-semibold">Confirm & Save</Label>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <p className="text-sm text-muted-foreground">Call Notes</p>
            <p className="text-sm">{disposition.substring(0, 150)}{disposition.length > 150 ? '...' : ''}</p>
          </div>
          
          <div className="flex gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Duration</p>
              <p className="font-medium">{formatDuration(callDuration)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">New Stage</p>
              <Badge style={{ backgroundColor: pipelineStages.find(s => s.slug === selectedNextStage)?.color }}>
                {pipelineStages.find(s => s.slug === selectedNextStage)?.name || selectedNextStage}
              </Badge>
            </div>
          </div>

          {selectedDate && selectedTime && followUpTitle && (
            <div className="border-t pt-3">
              <p className="text-sm text-muted-foreground">Scheduled Follow-up</p>
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="h-4 w-4 text-amber-600" />
                <span className="font-medium">
                  {new Date(`${selectedDate}T${selectedTime}`).toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  })} at {formatTimeDisplay(`${selectedDate}T${selectedTime}`)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{followUpTitle}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-green-600" />
            Call: {lead?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Lead Info */}
          <Card className="bg-muted/30">
            <CardContent className="p-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">{lead?.name}</p>
                  <p className="text-sm text-muted-foreground">{lead?.phone}</p>
                </div>
                <Badge>{pipelineStages.find(s => s.slug === lead?.status)?.name || lead?.status}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Call Controls - Only when not ended */}
          {callState !== 'ended' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="text-4xl font-mono font-bold text-center">
                {formatDuration(callDuration)}
              </div>
              
              {callState === 'idle' && (
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 rounded-full w-16 h-16"
                  onClick={startCall}
                  data-testid="btn-start-call"
                >
                  <Phone className="h-6 w-6" />
                </Button>
              )}
              
              {callState === 'calling' && (
                <>
                  <Button 
                    size="lg" 
                    variant="destructive"
                    className="rounded-full w-16 h-16 animate-pulse"
                    onClick={endCall}
                    data-testid="btn-end-call"
                  >
                    <PhoneOff className="h-6 w-6" />
                  </Button>
                  <p className="text-sm text-muted-foreground">Call in progress... Click to end</p>
                </>
              )}
            </div>
          )}

          {/* Post-Call Steps */}
          {callState === 'ended' && (
            <div className="space-y-4 border-t pt-4">
              {/* Mode Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium">Post-call mode</span>
                </div>
                <div className="flex gap-1 bg-muted rounded-lg p-1">
                  <Button
                    variant={postCallMode === PostCallMode.AVATAR ? "default" : "ghost"}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setPostCallMode(PostCallMode.AVATAR)}
                    data-testid="btn-avatar-mode"
                  >
                    🤖 Avatar
                  </Button>
                  <Button
                    variant={postCallMode === PostCallMode.CLASSIC ? "default" : "ghost"}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setPostCallMode(PostCallMode.CLASSIC)}
                    data-testid="btn-classic-mode"
                  >
                    📝 Classic
                  </Button>
                </div>
              </div>

              {/* Avatar Mode */}
              {postCallMode === PostCallMode.AVATAR && (
                <PostCallAvatar
                  lead={lead}
                  callDuration={callDuration}
                  pipelineStages={pipelineStages}
                  aiEnabled={aiEnabled}
                  apiKey={apiKey}
                  onSave={() => {
                    onUpdateLead && onUpdateLead();
                    onClose();
                  }}
                  onSkip={onClose}
                />
              )}

              {/* Classic Mode */}
              {postCallMode === PostCallMode.CLASSIC && (
                <>
                  {/* Progress Indicator */}
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${postCallStep === PostCallStep.DISPOSITION ? 'bg-amber-500' : 'bg-green-500'}`} />
                    <div className={`w-8 h-0.5 ${postCallStep !== PostCallStep.DISPOSITION ? 'bg-green-500' : 'bg-gray-200'}`} />
                    <div className={`w-3 h-3 rounded-full ${postCallStep === PostCallStep.SCHEDULE ? 'bg-amber-500' : postCallStep === PostCallStep.CONFIRM ? 'bg-green-500' : 'bg-gray-200'}`} />
                    <div className={`w-8 h-0.5 ${postCallStep === PostCallStep.CONFIRM ? 'bg-green-500' : 'bg-gray-200'}`} />
                    <div className={`w-3 h-3 rounded-full ${postCallStep === PostCallStep.CONFIRM ? 'bg-amber-500' : 'bg-gray-200'}`} />
                  </div>
                  <div className="flex justify-center gap-8 text-xs text-muted-foreground">
                    <span className={postCallStep === PostCallStep.DISPOSITION ? 'text-amber-600 font-medium' : ''}>Notes</span>
                    <span className={postCallStep === PostCallStep.SCHEDULE ? 'text-amber-600 font-medium' : ''}>Schedule</span>
                    <span className={postCallStep === PostCallStep.CONFIRM ? 'text-amber-600 font-medium' : ''}>Confirm</span>
                  </div>

                  {/* Step Content */}
                  {postCallStep === PostCallStep.DISPOSITION && renderDispositionStep()}
                  {postCallStep === PostCallStep.SCHEDULE && renderScheduleStep()}
                  {postCallStep === PostCallStep.CONFIRM && renderConfirmStep()}
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {callState === 'ended' && postCallMode === PostCallMode.CLASSIC && (
            <>
              {postCallStep === PostCallStep.DISPOSITION && (
                <>
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={handleSkipScheduling}
                    disabled={!disposition.trim() || savingEvent}
                  >
                    {savingEvent ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    Save without scheduling
                  </Button>
                  <Button 
                    onClick={handleNextStep}
                    disabled={!disposition.trim()}
                    data-testid="btn-next-step"
                  >
                    Next: Schedule
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </>
              )}

              {postCallStep === PostCallStep.SCHEDULE && (
                <>
                  <Button variant="outline" onClick={handlePreviousStep}>
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={handleSkipScheduling}
                    disabled={savingEvent}
                  >
                    Skip scheduling
                  </Button>
                  <Button 
                    onClick={handleNextStep}
                    disabled={!selectedDate || !selectedTime || !followUpTitle || conflicts.length > 0}
                    data-testid="btn-confirm-schedule"
                  >
                    Review & Confirm
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </>
              )}

              {postCallStep === PostCallStep.CONFIRM && (
                <>
                  <Button variant="outline" onClick={handlePreviousStep}>
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleSaveAndSchedule}
                    disabled={savingEvent}
                    data-testid="btn-save-all"
                  >
                    {savingEvent ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    Save & Schedule
                  </Button>
                </>
              )}
            </>
          )}

          {callState !== 'ended' && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CallAssistantDialog;
